/**
 * @description Handles variant selection and dispatches variant:change events
 */

import { dispatchVariantSelected, dispatchVariantUpdated } from "./events.js"
import { sectionRenderer, normalizeSectionId } from "./section-renderer.js"

/**
 * A simple variant picker component that demonstrates the Pent Core event system
 *
 * Usage:
 * <pent-variant-picker
 *   data-product-id="12345"
 *   data-section-id="shopify-section-template--123--product">
 *   <!-- variant selector inputs (select or radio buttons) -->
 * </pent-variant-picker>
 */
class PentVariantPicker extends HTMLElement {
  /**
   * Abort controller for pending requests
   * @type {AbortController | undefined}
   */
  #abortController

  connectedCallback() {
    // Listen for changes on variant selector inputs
    this.addEventListener("change", this.#handleVariantChange.bind(this))
  }

  /**
   * Handles variant selection changes
   * @param {Event} event - The change event
   */
  async #handleVariantChange(event) {
    const target = event.target
    if (!(target instanceof HTMLElement)) return

    this.#updateSelectedOption(target)

    const productId = this.dataset.productId
    const sectionId = this.dataset.sectionId
    const selectedVariantId = target.dataset.variantId

    if (!productId || !sectionId) {
      console.warn("PentVariantPicker: Missing required data-product-id or data-section-id")
      return
    }

    dispatchVariantSelected(target, {
      productId,
      variantId: selectedVariantId,
      sectionId: normalizeSectionId(sectionId),
    })

    // Update the URL with the selected variant
    this.#updateURL(selectedVariantId)

    // Fetch the updated section HTML
    try {
      await this.#fetchUpdatedSection(sectionId, selectedVariantId)

      // Dispatch the variant:change event
      // Use the section element as the target for scoped bubbling
      const sectionElement = document.getElementById(sectionId)
      const target = sectionElement || document

      dispatchVariantUpdated(target, {
        productId,
        variantId: selectedVariantId,
        sectionId: normalizeSectionId(sectionId),
      })
    } catch (error) {
      console.error("Failed to update variant:", error)
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  #updateSelectedOption(target) {
    if (target instanceof HTMLInputElement) {
      target.checked = true
    }
  }

  /**
   * Gets the current variant data from the JSON script tag
   * @returns {Object | null} The current variant data or null
   */
  #getCurrentVariant() {
    const scriptTag = this.querySelector('script[type="application/json"]')
    if (!scriptTag) return null

    try {
      return JSON.parse(scriptTag.textContent)
    } catch (error) {
      console.error("Failed to parse variant data:", error)
      return null
    }
  }

  /**
   * Updates the browser URL with the selected variant
   * @param {string} variantId - The selected variant ID
   */
  #updateURL(variantId) {
    const url = new URL(window.location.href)

    if (variantId) {
      url.searchParams.set("variant", variantId)
    } else {
      url.searchParams.delete("variant")
    }

    if (url.href !== window.location.href) {
      // Use replaceState to avoid polluting browser history
      window.history.replaceState({}, "", url.toString())
    }
  }

  /**
   * Fetches the updated section with the new variant
   * @param {string} sectionId - The section ID to update
   * @param {string} variantId - The selected variant ID
   * @returns {Promise<void>}
   *
   * ARCHITECTURE NOTE: Full Section Re-render Strategy
   * ================================================
   * This fetches and morphs the ENTIRE section HTML rather than individual
   * components. This approach ensures:
   *
   * 1. SERVER-DRIVEN TRUTH
   *    - Shopify's Liquid renders accurate data (prices, inventory, rules, etc.)
   *    - No JavaScript trying to calculate/predict what changed
   *    - Works with any custom blocks or metafields in the theme
   *
   * 2. THEME-AGNOSTIC
   *    - We don't need to know the theme's HTML structure
   *    - Works across any Shopify theme (Dawn, Impulse, custom, etc.)
   *    - Theme editor can add/remove blocks without breaking JS
   *
   * 3. EFFICIENT UPDATES
   *    - The morph algorithm only updates DOM nodes that actually changed
   *    - Unchanged elements stay untouched (preserves focus, scroll, etc.)
   *    - Network: 1 request instead of 3-5 per-component requests
   *
   * 4. DECOUPLED COMPONENTS
   *    - Each component just listens to 'variant:change' event
   *    - No component needs to know about others
   *    - No race conditions or sync problems
   *
   * Alternative: Components could fetch their own HTML, but that would require
   * knowing every theme's HTML structure, cause multiple requests, and create
   * sync issues. This approach is simpler and more robust.
   */
  async #fetchUpdatedSection(sectionId, variantId) {
    // Abort any pending request
    if (this.#abortController) {
      this.#abortController.abort()
    }

    this.#abortController = new AbortController()

    // Build URL with the variant parameter
    const url = new URL(window.location.href)
    url.searchParams.set("variant", variantId)

    // Use the section renderer to fetch and morph the section
    await sectionRenderer.renderSection(normalizeSectionId(sectionId), {
      url,
      cache: true, // Don't cache variant-specific HTML
    })

    this.#abortController = undefined
  }
}

// Register the custom element
if (!customElements.get("pent-variant-picker")) {
  customElements.define("pent-variant-picker", PentVariantPicker)
}

export default PentVariantPicker
